/**
 * dark-tokens.ts — the ONLY place a dark-band neutral (band/surface/figure/
 * ink) resolves (R6 de-slop round, judge ruling: warm retint of the whole
 * dark neutral system). Analog of `ui-light/light-tokens.ts` (the light-band
 * sibling registry) and `constants/pricing-type.ts` (the typography sibling
 * registry) — mirrors both files' discipline exactly: every consumer imports
 * the pre-baked Tailwind-class STRING (or, for compound recipes, the fully
 * pre-composed literal) from here — never hand-composes `bg-[${hex}]` or a
 * `linear-gradient(...)`/`color-mix(...)` string at a call site — so there is
 * exactly one place a dark neutral can drift.
 *
 * WARM_NEUTRAL_RAMP LAW (judge R6 ruling, enforced by
 * `scripts/check-landing-band-hue.mjs`'s WARM_NEUTRAL_RAMP exception): every
 * hex below sits at OKLCH hue H 65-90 with chroma 0.005 <= C <= 0.010 — a
 * "warm neutral, NOT sepia" retint, roughly 20x less saturated than the
 * amber accent's own C ~= 0.165. That exact (H, C) window is legal ONLY
 * inside THIS file — the gate fails closed on the identical hex typed raw
 * into a band/section component, forcing every consumer through this
 * registry instead of a second, driftable copy.
 *
 * OLD (cool) -> NEW (warm) traceability — every token this round retinted:
 *   Footer band                    #020202 -> #030201  (DARK_BAND.footer)
 *   Hero/final-CTA pole            #050505 -> #060503  (DARK_BAND.pole)
 *   Product/FAQ/Trust band         #08090B -> #0B0906  (DARK_BAND.section)
 *   Bot band                       #0A0B0D -> #0D0B08  (DARK_BAND.bot)
 *   Pricing band                   #0E0F11 -> #110F0B  (DARK_BAND.pricing)
 *   House Surface fill             #121418 -> #161410  (DARK_SURFACE.base)
 *   Raised/alternate surface       #16191E -> #1B1815  (DARK_SURFACE.raised)
 *   Figure gradient stop 1         #0F1011 -> #120F0C  (DARK_FIGURE.stop1)
 *   Figure gradient stop 2         #23252A -> #272521  (DARK_FIGURE.stop2)
 *   Neutral avatar-solid ink       #0B0C0E -> #0E0C08  (DARK_INK.onAccent)
 *   Brightest ink                  #F7F8FA -> #FBF7F2  (DARK_INK.primary)
 *   Bright ink (figures)           #F5F6F7 -> #F9F5F0  (DARK_INK.bright)
 *   Secondary ink                  #C4C8CF -> #CBC7C2  (DARK_INK.secondary)
 *   Tertiary ink                   #949BA6 -> #9D9A95  (DARK_INK.tertiary)
 *   Quaternary ink                 #737C88 -> #7E7B76  (DARK_INK.quaternary)
 *   One-off ink                    #98A1AE -> #A3A09B  (DARK_INK.oneOff)
 *
 * IMPORTANT: `DARK_INK.quaternary` (`#7E7B76`) is quaternary/decorative
 * ONLY — a pair-specific contrast check is required before any body-text
 * use. It is the lowest-contrast step in the ink ladder; treat it the same
 * way `light-tokens.ts` treats `ink.eyebrowDecorative` (`contentSafe: false`)
 * — dot fills, hairline accents, never a paragraph of readable copy.
 *
 * `DARK_COMPOUND` below is a second, deliberately separate export group: the
 * 3 real call sites this round (`ui/Chip.tsx`'s keycap gradient,
 * `ui/FigureFrame.tsx`'s mode-A inset hairline and mode-E backdrop) embed
 * more than one token inside a SINGLE Tailwind arbitrary-value bracket (a
 * two-stop `linear-gradient()`, an inset `shadow-[...]`, a `color-mix()`).
 * Interpolating `DARK_SURFACE`/`DARK_INK` hexes into a template literal at
 * those call sites would produce a class name that exists only at RUNTIME —
 * invisible to Tailwind's static JIT extractor, which only recognizes a
 * class shape that exists as UNBROKEN literal text somewhere in a scanned
 * file (the same reason `pricing-type.ts` and `light-tokens.ts` export
 * complete pre-baked strings instead of composing them at the call site).
 * Each `DARK_COMPOUND` constant is that unbroken literal text, verified
 * present in `tailwind.config.ts`'s `./src/features/**` content glob so this
 * file itself is scanned.
 *
 * Related: [[rules/enforcements]] #49 · `ui/tone.ts` (sibling registry,
 * sibling discipline, and this round's DARK_INK.onAccent /
 * DARK_SURFACE.raised consumer) · `ui-light/light-tokens.ts` (light-band
 * analog) · `constants/pricing-type.ts` (typography analog) ·
 * `scripts/check-landing-band-hue.mjs` (WARM_NEUTRAL_RAMP enforcer).
 */

/** A background-oriented dark token: raw hex (inline style / SVG / gradient
 * composition by a consumer that cannot pre-bake a static class) + the
 * pre-baked `bg-[#hex]` Tailwind class. */
export type DarkFillRecipe = {
  hex: string;
  bg: string;
};

/** A text-oriented dark ink token: raw hex + the pre-baked `text-[#hex]`
 * Tailwind class. */
export type DarkInkRecipe = {
  hex: string;
  text: string;
};

export type DarkBandRole = 'footer' | 'pole' | 'section' | 'bot' | 'pricing';
export type DarkSurfaceRole = 'base' | 'raised';
export type DarkFigureRole = 'stop1' | 'stop2';
export type DarkInkRole = 'primary' | 'bright' | 'secondary' | 'tertiary' | 'quaternary' | 'oneOff';

/**
 * The 5-step neutral luminance band ramp (masterplan §2's original scale,
 * warm-retinted this round). Chapter identity comes from composition/
 * density, never a tinted surface — this ramp stays a single warm-neutral
 * scale, not five different hues.
 */
export const DARK_BAND: Record<DarkBandRole, DarkFillRecipe> = {
  footer: { hex: '#030201', bg: 'bg-[#030201]' },
  pole: { hex: '#060503', bg: 'bg-[#060503]' },
  section: { hex: '#0B0906', bg: 'bg-[#0B0906]' },
  bot: { hex: '#0D0B08', bg: 'bg-[#0D0B08]' },
  pricing: { hex: '#110F0B', bg: 'bg-[#110F0B]' },
};

/** The two-tone card recipe (rest / hover-promote), warm-retinted this
 * round. `ui/Surface.tsx` (base) and `ui/tone.ts` + `ui/Avatar.tsx` +
 * `ui/Chip.tsx` (raised, reused as the neutral avatar/keycap fill) consume
 * these. NOTE: `ui/hover-grammar.ts`'s `HOVER_SURFACE`/`HOVER_LIFT`
 * hover-promote target is retinted in the SAME R6 wave via
 * `DARK_COMPOUND.hoverSurfaceRaisedBg` below (wave lane L3 rewires the
 * consumer) — resting fill and hover target stay one rung apart on the same
 * warm ladder, never a warm-rest/cool-hover split.
 */
export const DARK_SURFACE: Record<DarkSurfaceRole, DarkFillRecipe> = {
  base: { hex: '#161410', bg: 'bg-[#161410]' },
  raised: { hex: '#1B1815', bg: 'bg-[#1B1815]' },
};

/** `ui/FigureFrame.tsx`'s two figure-chrome tones (mode A's inner panel fill
 * + its inset hairline stroke), warm-retinted this round. Named `stop1`/
 * `stop2` per the R6 contract's own naming — both are currently consumed as
 * a solid fill / a shadow color respectively (see `DARK_COMPOUND` below for
 * the exact pre-composed shadow recipe), not literally as CSS gradient
 * stops today; the names are kept forward-looking in case a future figure
 * combines them into a real two-stop gradient. */
export const DARK_FIGURE: Record<DarkFigureRole, DarkFillRecipe> = {
  stop1: { hex: '#120F0C', bg: 'bg-[#120F0C]' },
  stop2: { hex: '#272521', bg: 'bg-[#272521]' },
};

/**
 * The ink hierarchy ladder (brightest -> most decorative), warm-retinted
 * this round. Six named steps, matching the R6 contract's suggested scheme
 * exactly. `quaternary` is decorative-only (see file header warning) — the
 * same discipline `light-tokens.ts` applies to `ink.eyebrowDecorative` via
 * its `contentSafe` flag, just without a separate boolean field here since
 * no current dark-ink consumer needs a compile-time content-safety gate.
 */
export const DARK_INK: Record<DarkInkRole, DarkInkRecipe> = {
  primary: { hex: '#FBF7F2', text: 'text-[#FBF7F2]' },
  bright: { hex: '#F9F5F0', text: 'text-[#F9F5F0]' },
  secondary: { hex: '#CBC7C2', text: 'text-[#CBC7C2]' },
  tertiary: { hex: '#9D9A95', text: 'text-[#9D9A95]' },
  /** Decorative ONLY — see file header warning. Never body text without an
   * independently-verified pair-specific contrast check. */
  quaternary: { hex: '#7E7B76', text: 'text-[#7E7B76]' },
  oneOff: { hex: '#A3A09B', text: 'text-[#A3A09B]' },
};

/**
 * The one ink role that is NOT part of the brightness ladder above: the
 * house's own darkest neutral, reused as near-black initials TEXT sitting on
 * top of a solid semantic-tone fill (`ui/tone.ts`'s `avatarSolidText`, paired
 * with `avatarSolidBg`) AND, separately, as a very-dark chrome-strip/backdrop
 * BACKGROUND inside `ui/FigureFrame.tsx` (mode B's readout strip, mode E's
 * translucent backdrop base). Named `onAccent` for its primary (tone.ts)
 * role; exposes both `text` and `bg` pre-baked forms since both are real,
 * shipped uses. Was `#0B0C0E`, "Neutral avatar-solid ink" in the R6 census.
 */
export const DARK_INK_ON_ACCENT: DarkInkRecipe & { bg: string } = {
  hex: '#0E0C08',
  text: 'text-[#0E0C08]',
  bg: 'bg-[#0E0C08]',
};

/**
 * Pre-composed COMPOUND recipes — the 3 real call sites this round whose
 * Tailwind arbitrary-value token embeds more than one primitive (a two-stop
 * gradient, an inset shadow, a color-mix backdrop). See the file header for
 * why these MUST be complete literal strings rather than interpolated at
 * the call site. Each doc comment below names the exact call site and its
 * pre-retint value for traceability.
 */
export const DARK_COMPOUND = {
  /** `ui/Chip.tsx`'s `keycap` variant fill. Was
   * `bg-[linear-gradient(#16191E,#121418)]` (raised -> base). */
  keycapGradientBg: 'bg-[linear-gradient(#1B1815,#161410)]',
  /** `ui/FigureFrame.tsx` mode A's inner-ring inset hairline. Was
   * `shadow-[inset_0_0_0_1px_#23252a]`. Color-only — the call site still
   * owns pairing this with its own `rounded-[12px] bg-[...]` geometry
   * classes. */
  figureModeAInsetShadow: 'shadow-[inset_0_0_0_1px_#272521]',
  /** `ui/FigureFrame.tsx` mode E's translucent backdrop fill. Was
   * `bg-[color-mix(in_srgb,#0B0C0E_68%,transparent)]`. Color-only — the
   * call site still owns composing this with its own `backdrop-blur-[8px]`
   * utility (that utility carries no hex, so it stays a literal at the call
   * site rather than living in this color-only registry). */
  figureModeEBackdropBg: 'bg-[color-mix(in_srgb,#0E0C08_68%,transparent)]',
  /** `components/LandingNavbar.tsx`'s scrolled/menu-open frost scrim (R6
   * judge §2.4). Color-only — the navbar owns composing this with its own
   * `border-white/10 backdrop-blur-xl backdrop-saturate-150` utilities
   * (those carry no hex, so they stay literals at the call site). The `/80`
   * opacity modifier must stay glued to the hex as one unbroken literal for
   * Tailwind's static extraction. Was `bg-[#050505]/95`. */
  navFrostBg: 'bg-[#060503]/80',
  /** `ui/hover-grammar.ts`'s card hover-promote target (`HOVER_SURFACE` /
   * `HOVER_LIFT`) — one rung up the surface ladder, kept in lockstep with
   * `DARK_SURFACE.raised`. The `hover:` variant must stay glued to the hex
   * as one unbroken literal for Tailwind's static extraction. Was
   * `hover:bg-[#16191E]`. */
  hoverSurfaceRaisedBg: 'hover:bg-[#1B1815]',
} as const;

/**
 * Pre-baked VARIANT-PREFIXED single-hex recipes — R6 integration fix (closes
 * this round's own gate blind spot: `scripts/check-landing-band-hue.mjs`'s
 * WARM_NEUTRAL_RAMP section originally scanned only `bg-[#hex]`, so a
 * `ring-offset-[#hex]`/`hover:text-[#hex]`/`ring-[#hex]`/
 * `placeholder:text-[#hex]` site could carry a raw registry hex undetected).
 * Distinct from `DARK_COMPOUND` above: every `DARK_COMPOUND` entry embeds
 * MULTIPLE primitives inside one bracket (a two-stop gradient, an inset
 * shadow, a color-mix backdrop); every entry here embeds exactly ONE hex,
 * just with a Tailwind pseudo-class/state variant glued to it, for the SAME
 * static-JIT reason documented in the file header — interpolating
 * `${DARK_BAND.pole.hex}` into `` `ring-offset-[${hex}]` `` at a call site
 * produces a class that exists only at runtime, invisible to Tailwind's
 * scanner. Before this group existed, several call sites hardcoded the raw
 * hex literally with a doc-comment explaining the workaround
 * (TrustBlockSection.tsx, FinalCTASection.tsx) — those comments described a
 * real gap; this group is the fix. Mint ONLY what a real call site needs —
 * no speculative variants.
 */
export const DARK_VARIANT = {
  /** Footer band ring-offset — FooterSection.tsx's Mast + Default variants
   * (logo, nav/legal links, LinkedIn, mailto, affiliate link — every
   * focus-visible ring rendered directly on the footer band). Was raw
   * `focus-visible:ring-offset-[#030201]` (post-retint) / `#020202` (pre). */
  footerRingOffset: 'focus-visible:ring-offset-[#030201]',
  /** Assurance/Product/FAQ section band ring-offset — TrustBlockSection.tsx's
   * legal links, FAQSection.tsx's accordion triggers. Was raw
   * `focus-visible:ring-offset-[#0B0906]` (post-retint) / `#08090B` (pre). */
  sectionRingOffset: 'focus-visible:ring-offset-[#0B0906]',
  /** Hero/final-CTA pole band ring-offset — LandingNavbar.tsx (logo, desktop
   * links, mobile menu button, mobile links), HeroSection.tsx's secondary
   * CTA, FinalCTASection.tsx's secondary CTA. Was raw
   * `focus-visible:ring-offset-[#060503]` (post-retint) / `#050505` (pre). */
  poleRingOffset: 'focus-visible:ring-offset-[#060503]',
  /** Bot band ring-offset — SolutionSection.tsx's module-activation CTA,
   * WhatsAppDemoFormStaged.tsx's staged CTA card. Was raw
   * `focus-visible:ring-offset-[#0D0B08]` (post-retint) / `#0A0B0D` (pre). */
  botRingOffset: 'focus-visible:ring-offset-[#0D0B08]',
  /** House Surface (card) ring-offset — PricingSection.tsx's non-popular
   * tier CTA (sits on the Surface card, not the pricing band). Was raw
   * `focus-visible:ring-offset-[#161410]` (post-retint) / `#121418` (pre). */
  surfaceRingOffset: 'focus-visible:ring-offset-[#161410]',
  /** House Surface (card) plain ring — avatar-stack separators
   * (HeroLeadCardV2.tsx's two assignee avatars, KanbanCardFigure.tsx's
   * footer assignee pills). Call site still owns its own `ring-2` width
   * utility; this token is color-only. Was raw `ring-[#161410]`
   * (post-retint) / `#121418` (pre). */
  surfaceRing: 'ring-[#161410]',
  /** Brightest ink hover — FooterSection.tsx's + TrustBlockSection.tsx's
   * link hover states, WhatsAppDemoForm.tsx's tab-switcher hover. Was raw
   * `hover:text-[#FBF7F2]` (post-retint) / `#F7F8FA` (pre). */
  primaryHoverText: 'hover:text-[#FBF7F2]',
  /** Tertiary ink hover — FooterSection.tsx's affiliate-link underline
   * hover. Was raw `hover:text-[#9D9A95]` (post-retint) / `#949BA6` (pre). */
  tertiaryHoverText: 'hover:text-[#9D9A95]',
  /** Quaternary ink placeholder — WhatsAppDemoForm.tsx's name/phone input
   * hint text, rendered on `DARK_BAND.bot.bg`. `DARK_INK.quaternary` is
   * decorative-only (file header) but placeholder/hint text is a legitimate
   * decorative-adjacent use (never required-content text) — the pairing
   * against `DARK_BAND.bot` (`#0D0B08`) measures ~4.6:1, clearing the 4.5:1
   * AA normal-text threshold despite the decorative-only ink. Was raw
   * `placeholder:text-[#7E7B76]` (post-retint) / `#737C88` (pre). */
  quaternaryPlaceholderText: 'placeholder:text-[#7E7B76]',
} as const;

export type DarkTokens = {
  band: typeof DARK_BAND;
  surface: typeof DARK_SURFACE;
  figure: typeof DARK_FIGURE;
  ink: typeof DARK_INK;
  inkOnAccent: typeof DARK_INK_ON_ACCENT;
  compound: typeof DARK_COMPOUND;
  variant: typeof DARK_VARIANT;
};
